import { ToolEvent } from '@/types/msq'
import { FinancialDataResponse, ToolResultContent, ProcessedCandleData, CandleData } from '../types/chart'

/**
 * List of tool names that return financial OHLCV data
 */
export const FINANCIAL_TOOL_NAMES = [
  'coreStock_intraday',
  'coreStock_daily',
  'coreStock_weekly',
  'coreStock_monthly',
  'crypto_intraday',
  'crypto_daily',
  'crypto_weekly',
  'crypto_monthly'
]

/**
 * Check if a tool event contains financial data
 */
export function isFinancialToolEvent(event: ToolEvent): boolean {
  return (
    event.type === 'tool_end' &&
    event.data?.toolName &&
    FINANCIAL_TOOL_NAMES.includes(event.data.toolName)
  )
}

/**
 * Extract financial data from a tool event
 * Returns null if the event doesn't contain valid financial data
 */
export function extractFinancialData(event: ToolEvent): FinancialDataResponse | null {
  try {
    if (!isFinancialToolEvent(event)) {
      return null
    }

    const result = event.data?.result
    if (!result?.content || !Array.isArray(result.content)) {
      return null
    }

    // Find the content item with financial data
    const dataContent = result.content.find((item: any) => 
      item.type === 'text' && 
      item.text?.meta_data && 
      item.text?.time_series
    )

    if (!dataContent) {
      return null
    }

    const { meta_data, time_series } = dataContent.text

    // Validate time_series is an array with at least one item
    if (!Array.isArray(time_series) || time_series.length === 0) {
      return null
    }

    // Validate first item has required OHLCV fields
    const firstCandle = time_series[0]
    if (!validateCandleData(firstCandle)) {
      return null
    }

    return {
      meta_data,
      time_series
    }
  } catch (error) {
    console.error('Error extracting financial data:', error)
    return null
  }
}

/**
 * Validate that a candle has all required OHLCV fields
 */
export function validateCandleData(candle: any): candle is CandleData {
  return (
    candle &&
    typeof candle.timestamp === 'string' &&
    typeof candle.open === 'number' &&
    typeof candle.high === 'number' &&
    typeof candle.low === 'number' &&
    typeof candle.close === 'number' &&
    typeof candle.volume === 'number' &&
    candle.high >= candle.low &&
    candle.high >= candle.open &&
    candle.high >= candle.close &&
    candle.low <= candle.open &&
    candle.low <= candle.close
  )
}

/**
 * Process raw candle data by adding Date objects
 */
export function processCandleData(data: CandleData[]): ProcessedCandleData[] {
  return data.map(candle => ({
    ...candle,
    date: new Date(candle.timestamp)
  }))
}

/**
 * Get symbol from meta data (handles both crypto and stock formats)
 */
export function getSymbolFromMetaData(meta_data: any): string {
  if ('digital_currency_code' in meta_data) {
    return `${meta_data.digital_currency_code}/${meta_data.market_code}`
  }
  if ('symbol' in meta_data) {
    return meta_data.symbol
  }
  return 'Unknown'
}

/**
 * Format number for display (with appropriate decimal places)
 */
export function formatPrice(value: number): string {
  if (value >= 1000) {
    return value.toFixed(0)
  } else if (value >= 1) {
    return value.toFixed(2)
  } else {
    return value.toFixed(4)
  }
}

/**
 * Format volume for display
 */
export function formatVolume(value: number): string {
  if (value >= 1e9) {
    return `${(value / 1e9).toFixed(2)}B`
  } else if (value >= 1e6) {
    return `${(value / 1e6).toFixed(2)}M`
  } else if (value >= 1e3) {
    return `${(value / 1e3).toFixed(2)}K`
  }
  return value.toFixed(0)
}
